/**
 * Simplified FSRS (Free Spaced Repetition Scheduler) implementation.
 * Based on the FSRS-4.5 algorithm by Jarrett Ye.
 */

const DECAY = -0.5;
const FACTOR = 0.9 ** (1 / DECAY) - 1;
const DESIRED_RETENTION = 0.9;

// Default FSRS parameters (w0-w12)
const DEFAULTS = {
  w: [0.4, 0.6, 2.4, 5.8, 4.93, 0.94, 0.86, 0.01, 1.49, 0.14, 0.94, 2.18, 0.05],
};

export const CardState = {
  NEW: 0,
  LEARNING: 1,
  REVIEW: 2,
  RELEARNING: 3,
};

export const Rating = {
  AGAIN: 1,
  HARD: 2,
  GOOD: 3,
  EASY: 4,
};

export function createNewCardState() {
  return {
    state: CardState.NEW,
    stability: 0,
    difficulty: 0,
    due: new Date().toISOString(),
    reps: 0,
    lapses: 0,
    lastReview: null,
    elapsedDays: 0,
    scheduledDays: 0,
  };
}

function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}

function calcRetrievability(elapsedDays, stability) {
  if (stability <= 0) return 0;
  return (1 + FACTOR * (elapsedDays / stability)) ** DECAY;
}

function initDifficulty(rating) {
  const w = DEFAULTS.w;
  return clamp(w[4] - (rating - 3) * w[5], 1, 10);
}

function initStability(rating) {
  const w = DEFAULTS.w;
  return Math.max(w[rating - 1], 0.1);
}

function nextDifficulty(d, rating) {
  const w = DEFAULTS.w;
  const newD = d - w[6] * (rating - 3);
  return clamp(w[4] * (1 - w[7]) + newD * w[7], 1, 10);
}

function nextRecallStability(d, s, r, rating) {
  const w = DEFAULTS.w;
  const hardPenalty = rating === Rating.HARD ? w[11] : 1;
  const easyBonus = rating === Rating.EASY ? w[12] : 1;
  return s * (1 + Math.exp(w[8]) * (11 - d) * Math.pow(s, -w[9]) * (Math.exp((1 - r) * w[10]) - 1) * hardPenalty * easyBonus);
}

// eslint-disable-next-line no-unused-vars
function nextForgetStability(d, s, r) {
  const w = DEFAULTS.w;
  return w[11] * Math.pow(d, -w[12]) * (Math.pow(s + 1, w[13] || 0.2) - 1) * Math.exp((1 - r) * (w[14] || 0.5));
}

function calcInterval(stability) {
  const interval = (stability / FACTOR) * (Math.pow(DESIRED_RETENTION, 1 / DECAY) - 1);
  return Math.max(Math.round(interval), 1);
}

export function scheduleCard(cardState, rating) {
  const now = new Date();
  const state = { ...cardState };

  let elapsedDays = 0;
  if (state.lastReview) {
    elapsedDays = Math.max(0, (now - new Date(state.lastReview)) / (1000 * 60 * 60 * 24));
  }

  let newStability, newDifficulty, newState, scheduledDays;

  if (state.state === CardState.NEW) {
    newDifficulty = initDifficulty(rating);
    newStability = initStability(rating);

    if (rating === Rating.AGAIN) {
      scheduledDays = 0;
      newState = CardState.LEARNING;
    } else if (rating === Rating.HARD) {
      scheduledDays = 1;
      newState = CardState.LEARNING;
    } else if (rating === Rating.GOOD) {
      scheduledDays = calcInterval(newStability);
      newState = CardState.REVIEW;
    } else {
      scheduledDays = calcInterval(newStability);
      newState = CardState.REVIEW;
    }
  } else if (state.state === CardState.LEARNING || state.state === CardState.RELEARNING) {
    newDifficulty = nextDifficulty(state.difficulty, rating);

    if (rating === Rating.AGAIN) {
      newStability = Math.max(initStability(Rating.AGAIN), state.stability * 0.5);
      scheduledDays = 0;
      newState = state.state;
    } else if (rating === Rating.HARD) {
      newStability = Math.max(state.stability, initStability(Rating.HARD));
      scheduledDays = 1;
      newState = state.state;
    } else if (rating === Rating.GOOD) {
      newStability = Math.max(state.stability * 1.5, initStability(Rating.GOOD));
      scheduledDays = calcInterval(newStability);
      newState = CardState.REVIEW;
    } else {
      newStability = Math.max(state.stability * 2, initStability(Rating.EASY));
      scheduledDays = calcInterval(newStability);
      newState = CardState.REVIEW;
    }
  } else {
    // Review state
    const r = calcRetrievability(elapsedDays, state.stability);
    newDifficulty = nextDifficulty(state.difficulty, rating);

    if (rating === Rating.AGAIN) {
      newStability = Math.max(0.1, state.stability * 0.3);
      scheduledDays = 0;
      newState = CardState.RELEARNING;
    } else {
      newStability = nextRecallStability(state.difficulty, state.stability, r, rating);
      newStability = Math.max(newStability, state.stability * 1.1);
      scheduledDays = calcInterval(newStability);
      newState = CardState.REVIEW;
    }
  }

  const dueDate = new Date(now);
  if (scheduledDays === 0) {
    dueDate.setMinutes(dueDate.getMinutes() + 10);
  } else {
    dueDate.setDate(dueDate.getDate() + scheduledDays);
  }

  return {
    state: newState,
    stability: newStability,
    difficulty: newDifficulty,
    due: dueDate.toISOString(),
    reps: rating === Rating.AGAIN ? state.reps : state.reps + 1,
    lapses: rating === Rating.AGAIN ? state.lapses + 1 : state.lapses,
    lastReview: now.toISOString(),
    elapsedDays,
    scheduledDays,
  };
}

export function getDueCards(cards) {
  const now = new Date();
  return cards.filter(card => {
    if (card.fsrs.state === CardState.NEW) return true;
    return new Date(card.fsrs.due) <= now;
  });
}

export function getCardCounts(cards) {
  let newCount = 0, learningCount = 0, dueCount = 0;
  const now = new Date();

  for (const card of cards) {
    if (card.fsrs.state === CardState.NEW) {
      newCount++;
    } else if (card.fsrs.state === CardState.LEARNING || card.fsrs.state === CardState.RELEARNING) {
      learningCount++;
    } else if (new Date(card.fsrs.due) <= now) {
      dueCount++;
    }
  }

  return { newCount, learningCount, dueCount, total: cards.length };
}

export function formatInterval(days) {
  if (days < 1) return '< 1d';
  if (days < 30) return `${Math.round(days)}d`;
  if (days < 365) return `${Math.round(days / 30)}mo`;
  return `${(days / 365).toFixed(1)}yr`;
}
